import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { DraggableComponents } from '../../../../common/types/checkout';

type Props = {
  component: DraggableComponents;
};

const BAR_W = 3;
const GAP_W = 2;
const MIN_BARS = 40;
const MAX_BARS = 140;

export const AudioSocialProof: React.FC<Props> = ({ component }) => {
  const [playingAudio, setPlayingAudio] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioProgress, setAudioProgress] = useState<number>(0);
  const [audioCurrentTimes, setAudioCurrentTimes] = useState<number>(0);
  const [audioDurations, setAudioDurations] = useState<number>(0);

  const avatarVal = component?.configurations?.testimonial?.avatar;
  const audioVal = component?.configurations?.testimonial?.audioUrl;

  const trackRef = useRef<HTMLDivElement | null>(null);
  const [barsCount, setBarsCount] = useState<number>(MIN_BARS);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const calc = () => {
      const width = el.clientWidth || 0;
      const candidate = Math.floor((width + GAP_W) / (BAR_W + GAP_W));
      const next = Math.max(MIN_BARS, Math.min(MAX_BARS, candidate));
      setBarsCount(next);
    };

    const ro = new ResizeObserver(calc);
    ro.observe(el);
    window.addEventListener('resize', calc);
    calc();

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', calc);
    };
  }, []);

  useEffect(() => {
    setPlayingAudio(false);
    setAudioProgress(0);
    setAudioCurrentTimes(0);
    setAudioDurations(0);
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.currentTime = 0;
    }
  }, [audioVal]);

  const formatAudioTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayAudio = () => {
    const audio = audioRef.current;
    if (!audio || !audioVal) return;

    if (playingAudio) {
      audio.pause();
      setPlayingAudio(false);
    } else {
      audio
        .play()
        .then(() => setPlayingAudio(true))
        .catch(() => {
          return;
        });
    }
  };

  const p = Math.min(100, Math.max(0, audioProgress || 0));

  return (
    <AudioTestimonialsGrid>
      <AudioTestimonialCard>
        <WhatsAppAudioPlayer>
          <AudioMainRow>
            <AvatarContainer>
              <AudioAvatar
                src={avatarVal}
                alt={component?.configurations?.testimonial?.name}
              />
              <MicrophoneIcon played={p > 0}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                  <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                </svg>
              </MicrophoneIcon>
            </AvatarContainer>

            <PlayButtonLarge onClick={handlePlayAudio} disabled={!audioVal}>
              {playingAudio ? (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                  <rect x="6" y="4" width="4" height="16" rx="1"></rect>
                  <rect x="14" y="4" width="4" height="16" rx="1"></rect>
                </svg>
              ) : (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </PlayButtonLarge>

            <AudioWaveformContainer ref={trackRef}>
              <AudioWaveformRow>
                {Array.from({ length: barsCount }).map((_, i) => (
                  <WaveBar
                    key={i}
                    height={`${35 + (i % 3) * 35}%`}
                    played={p > (i / barsCount) * 100}
                  />
                ))}
              </AudioWaveformRow>
              <ProgressBall style={{ left: `${p}%` }} progress={p} />
            </AudioWaveformContainer>
          </AudioMainRow>

          <BottomRow>
            <LeftSection>
              <AudioTestimonialName>
                {component?.configurations?.testimonial?.name}
              </AudioTestimonialName>
              <AudioCurrentTime>
                {formatAudioTime(audioCurrentTimes || 0)}
              </AudioCurrentTime>
            </LeftSection>
            <RightSection>
              <AudioDuration>
                {formatAudioTime(audioDurations || 0)}
              </AudioDuration>
              <CheckMarks played={p > 0}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path
                    d="M17 1L6 12L1 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13 1L6 8"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </CheckMarks>
            </RightSection>
          </BottomRow>

          <audio
            ref={audioRef}
            src={audioVal}
            onPlay={() => setPlayingAudio(true)}
            onPause={() => setPlayingAudio(false)}
            onEnded={() => setPlayingAudio(false)}
            onLoadedMetadata={(e) => {
              const a = e.currentTarget;
              setAudioDurations(isFinite(a.duration) ? a.duration : 0);
            }}
            onTimeUpdate={(e) => {
              const a = e.currentTarget;
              const progress = a.duration
                ? (a.currentTime / a.duration) * 100
                : 0;
              setAudioProgress(progress);
              setAudioCurrentTimes(a.currentTime);
            }}
            style={{ display: 'none' }}
          />
        </WhatsAppAudioPlayer>
      </AudioTestimonialCard>
    </AudioTestimonialsGrid>
  );
};

const AudioTestimonialName = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
`;

const ProgressBall = styled.div<{ progress: number }>`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 14px;
  height: 14px;
  background: ${(p) => (p.progress > 0 ? '#53bdeb' : '#9ca3af')};
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  transition:
    left 0.1s linear,
    background 0.3s;
  pointer-events: none;
  z-index: 2;
`;

const WaveBar = styled.div<{ height: string; played: boolean }>`
  width: ${BAR_W}px;
  height: ${(p) => p.height};
  background: ${(p) => (p.played ? 'rgb(153, 190, 183)' : 'rgb(77, 141, 129)')};
  border-radius: 1px;
  transition: background 0.2s;
`;

const AudioTestimonialsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 10px;
  max-width: 100%;
  overflow: hidden;
`;

const AudioTestimonialCard = styled.div`
  background: transparent;
  padding: 0;
  max-width: 100%;
  overflow: hidden;
`;

const WhatsAppAudioPlayer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  background: rgb(20, 77, 55);
  padding: 6px 12px 10px 12px;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  min-height: 88px;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
`;

const AudioMainRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  margin-top: 8px;
`;

const AvatarContainer = styled.div`
  position: relative;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
`;

const MicrophoneIcon = styled.div<{ played: boolean }>`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 18px;
  height: 18px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);

  svg {
    color: ${(props) => (props.played ? '#53bdeb' : '#9ca3af')};
    transition: color 0.3s;
  }
`;

const BottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  padding-left: 0px;
`;

const PlayButtonLarge = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  margin-left: 4px;
  width: 46px;
  height: 46px;

  svg {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
  }

  &:hover {
    transform: scale(1.08);
  }

  &:active {
    transform: scale(0.96);
  }
`;

const AudioWaveformContainer = styled.div`
  position: relative;
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  margin-right: 4px;
  height: 36px;
`;

const AudioWaveformRow = styled.div`
  height: 36px;
  display: flex;
  align-items: center;
  gap: ${GAP_W}px;
  width: 100%;
`;

const AudioAvatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
`;

const AudioCurrentTime = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;
`;

const AudioDuration = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;
`;

const CheckMarks = styled.div<{ played: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: ${(props) => (props.played ? '#53bdeb' : '#9ca3af')};
    transition: color 0.3s;
  }
`;
